import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import Pagination from "../components/Pagination";
import AcademicHeader from "../Academic/AcademicHeader";
import {
  feedbackAllList,
  feedbackDelete,
  addFeedback,
} from "../../api/FeedbackService";
import { studentsListOnly } from "../../api/Student";
import { StaffList } from "../../api/Staff";
import Swal from "sweetalert2";
import { BatchStudent } from "../../api/StudentServices";

import "../Feedback/FeedbackView.css";
import usePrivateLink from "../config/usePrivateLink";
import { imgBaseUrl } from "../../api/api";

const getImageUrl = (person) => {
  const img = person?.profile_picture || person?.img;
  return img ? `${imgBaseUrl}${img}` : null;
};

const Avatar = ({ person, name, size }) => {
  const [failed, setFailed] = useState(false);
  const src = getImageUrl(person);

  // Reset error state when the image source changes
  useEffect(() => {
    setFailed(false);
  }, [src]);

  const initial = (name || person?.name || person?.staffname || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <div
      className="feedback-avatar"
      style={size ? { width: size, height: size } : undefined}
    >
      {src && !failed ? (
        <img
          src={src}
          alt={name || "avatar"}
          onError={() => setFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "inherit",
          }}
        />
      ) : (
        initial
      )}
    </div>
  );
};

const getBatchTitle = (p) =>
  p?.batch_title ||
  p?.student_batch_info?.batch?.title ||
  p?.batch_name ||
  null;

export default function FeedbackView() {
  const location = useLocation();
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [filters, setFilters] = useState({
    search: "",
    data_type: "student",
  });

  const [staffList, setStaffList] = useState([]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const userPermissions = localStorage.getItem("user_permissions");
    const parsedPermissions = userPermissions
      ? JSON.parse(userPermissions)
      : null;

    const roleId = parsedPermissions?.id;

    setIsSuperAdmin(Number(roleId) === 1);
  }, []);

  // =============================
  // Add Feedback States
  // =============================
  const [showAddFeedbackModal, setShowAddFeedbackModal] = useState(false);

  const [userData, setUserData] = useState(null);

  const [feedbackForm, setFeedbackForm] = useState({
    target_type: "",
    target_id: "",
    subject: "",
    message: "",
    rating: 5,
    type: "suggestion",
    category: "Suggestion",
    is_anonymous: "No",
  });

  const [feedbackStudentSearch, setFeedbackStudentSearch] = useState("");
  const [feedbackStudents, setFeedbackStudents] = useState([]);
  const [showFeedbackStudentResult, setShowFeedbackStudentResult] =
    useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user_data");

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);

        setUserData(parsedUser);

        // console.log("Logged in user:", parsedUser);
      }
    } catch (error) {
      console.error("Error reading user_data:", error);
    }
  }, []);

  const getStaffList = async () => {
    try {
      const res = await StaffList();

      if (res?.data?.status) {
        setStaffList(res.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const handleStudentSearch = async (value) => {
    setStudentSearch(value);

    if (value.length < 2) {
      setStudents([]);
      setShowResult(false);
      return;
    }

    try {
      const response = await BatchStudent(`search=${value}`);

      if (response.data.status) {
        setStudents(response.data.data);
        setShowResult(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFeedbackStudentSearch = async (value) => {
    setFeedbackStudentSearch(value);

    if (value.length < 2) {
      setFeedbackStudents([]);
      setShowFeedbackStudentResult(false);
      return;
    }

    try {
      const response = await BatchStudent(
        `search=${encodeURIComponent(value)}`,
      );

      if (response?.data?.status) {
        setFeedbackStudents(response.data.data || []);
        setShowFeedbackStudentResult(true);
      }
    } catch (error) {
      console.log("Feedback student search error:", error);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      if (studentSearch.length > 1) {
        handleStudentSearch(studentSearch);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [studentSearch]);

  useEffect(() => {
    getStaffList();
  }, []);

  const getList = async () => {
    setLoading(true);

    try {
      let addUrl = ``;

      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          addUrl += `&${key}=${encodeURIComponent(filters[key])}`;
        }
      });

      const response = await feedbackAllList(addUrl);

      if (response.data.status) {
        setData(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      const res = await feedbackDelete({ id });

      if (res.data.status) {
        SweetAlertShow("success", "Success", "Feedback deleted");

        getList();
      } else {
        SweetAlertShow("error", "Error", res.data.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getList();
  }, [filters]);

  const handleSubmitFeedback = async () => {
    // Check logged in user
    if (!userData?.id) {
      SweetAlertShow(
        "error",
        "Error",
        "User information not found. Please login again.",
      );
      return;
    }

    // Target validation
    if (!feedbackForm.target_type) {
      SweetAlertShow(
        "warning",
        "Required",
        "Please select who you want to give feedback to.",
      );
      return;
    }

    // Student must have target ID
    if (feedbackForm.target_type === "student" && !feedbackForm.target_id) {
      SweetAlertShow("warning", "Required", "Please select a student.");
      return;
    }

    // Subject
    if (!feedbackForm.subject.trim()) {
      SweetAlertShow("warning", "Required", "Please enter subject.");
      return;
    }

    // Message
    if (!feedbackForm.message.trim()) {
      SweetAlertShow("warning", "Required", "Please enter feedback message.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        // From localStorage
        auth_user_id: userData.id,

        // Teacher is submitting feedback
        auth_type: "teacher",

        // Selected target
        target_type: feedbackForm.target_type,

        // Only student has target_id
        target_id:
          feedbackForm.target_type === "student"
            ? feedbackForm.target_id
            : null,

        subject: feedbackForm.subject,
        message: feedbackForm.message,
        rating: feedbackForm.rating,
        type: feedbackForm.type,
        category: feedbackForm.category,
        is_anonymous: feedbackForm.is_anonymous,
      };

      console.log("Feedback Payload:", payload);

      const response = await addFeedback(payload);

      if (response?.data?.status) {
        SweetAlertShow("success", "Success", "Feedback added successfully.");

        // Close modal
        setShowAddFeedbackModal(false);

        // Reset form
        setFeedbackForm({
          target_type: "",
          target_id: "",
          subject: "",
          message: "",
          rating: 5,
          type: "suggestion",
          category: "Suggestion",
          is_anonymous: "No",
        });

        // Reset student search
        setFeedbackStudentSearch("");
        setFeedbackStudents([]);
        setShowFeedbackStudentResult(false);

        // Refresh feedback list
        getList();
      } else {
        SweetAlertShow(
          "error",
          "Error",
          response?.data?.message || "Unable to add feedback.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // UI HELPERS
  // =====================================================

  const getTypeClass = (type) => {
    switch (type) {
      case "complaint":
        return "feedback-type-danger";
      case "suggestion":
        return "feedback-type-primary";
      case "appreciation":
        return "feedback-type-success";
      default:
        return "feedback-type-secondary";
    }
  };

  const getRatingText = (rating) => {
    switch (Number(rating)) {
      case 1:
        return "Poor";
      case 2:
        return "Below Average";
      case 3:
        return "Average";
      case 4:
        return "Good";
      case 5:
        return "Excellent";
      default:
        return "Not Rated";
    }
  };

  const RatingStars = ({
    rating = 0,
    interactive = false,
    onChange,
    size = 18,
  }) => {
    return (
      <div
        className={`feedback-stars ${
          interactive ? "feedback-stars-interactive" : ""
        }`}
      >
        {[1, 2, 3, 4, 5].map((star) => {
          const active = Number(rating) >= star;

          return (
            <button
              key={star}
              type={interactive ? "button" : undefined}
              className={`feedback-star ${
                active ? "active" : ""
              } ${interactive && Number(rating) === star ? "selected" : ""}`}
              style={{ fontSize: `${size}px` }}
              onClick={interactive ? () => onChange?.(star) : undefined}
              aria-label={`Rate ${star} out of 5`}
            >
              <i className={active ? "ri-star-fill" : "ri-star-line"}></i>
            </button>
          );
        })}

        {!interactive && (
          <span className="feedback-rating-number">{rating}/5</span>
        )}
      </div>
    );
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid feedback-page">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-12">
                    {/* =====================================================
            HEADER + FILTER CARD
        ===================================================== */}
                    <div className="card feedback-card">
                      {/* Header */}
                      <div className="feedback-header">
                        <div className="d-flex justify-content-between align-items-center gap-3">
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <div className="feedback-avatar">
                                <i className="ri-feedback-line"></i>
                              </div>

                              <div>
                                <div className="feedback-title">
                                  Feedback Management
                                </div>

                                <div className="feedback-subtitle">
                                  View, manage and respond to feedback
                                </div>
                              </div>
                            </div>
                          </div>

                          {hasAccess("/feedback/add") && (
                            <button
                              type="button"
                              className="btn btn-primary feedback-add-btn"
                              onClick={() => setShowAddFeedbackModal(true)}
                            >
                              <i className="ri-add-line me-1"></i>
                              <span>Add Feedback</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="px-4 pt-3">
                        <div className="feedback-tabs">
                          <button
                            type="button"
                            className={`feedback-tab ${
                              filters.data_type === "student" ? "active" : ""
                            }`}
                            onClick={() =>
                              setFilters({
                                ...filters,
                                data_type: "student",
                                staff_id: "",
                                student_id: "",
                                page: 1,
                              })
                            }
                          >
                            <i className="ri-user-line me-1"></i>
                            Student Feedback
                          </button>

                          <button
                            type="button"
                            className={`feedback-tab ${
                              filters.data_type === "teacher" ? "active" : ""
                            }`}
                            onClick={() =>
                              setFilters({
                                ...filters,
                                data_type: "teacher",
                                staff_id: "",
                                student_id: "",
                                page: 1,
                              })
                            }
                          >
                            <i className="ri-user-star-line me-1"></i>
                            Teacher Feedback
                          </button>
                        </div>
                      </div>

                      {/* Filters */}
                      <div className="feedback-filter-area mt-3">
                        <div className="row g-3 align-items-end">
                          {/* Search */}
                          <div className="col-lg-5 col-md-6">
                            <div className="feedback-filter-label">Search</div>

                            <div className="feedback-search">
                              <i className="ri-search-line"></i>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search subject, type or feedback..."
                                value={filters?.search || ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    search: e.target.value,
                                    page: 1,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* Student Filter */}
                          {filters.data_type === "teacher" && (
                            <div className="col-lg-4 col-md-6 position-relative">
                              <div className="feedback-filter-label">
                                Student
                              </div>

                              <div className="feedback-search">
                                <i className="ri-user-search-line"></i>

                                <input
                                  type="text"
                                  className="form-control"
                                  value={studentSearch}
                                  placeholder="Search student..."
                                  onChange={(e) =>
                                    handleStudentSearch(e.target.value)
                                  }
                                />
                              </div>

                              {showResult && students.length > 0 && (
                                <div
                                  className="card position-absolute w-100 mt-1 shadow"
                                  style={{
                                    zIndex: 1050,
                                    maxHeight: "250px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <div className="list-group list-group-flush">
                                    {students.map((stu) => (
                                      <button
                                        type="button"
                                        key={stu.id}
                                        className="list-group-item list-group-item-action text-start"
                                        onClick={() => {
                                          setFilters({
                                            ...filters,
                                            student_id: stu.id,
                                            page: 1,
                                          });

                                          setStudentSearch(
                                            `${stu.name} (${stu.registration_no})${
                                              getBatchTitle(stu)
                                                ? ` - ${getBatchTitle(stu)}`
                                                : ""
                                            }`,
                                          );

                                          setStudents([]);
                                          setShowResult(false);
                                        }}
                                      >
                                        <div className="d-flex align-items-center gap-2">
                                          <Avatar
                                            person={stu}
                                            name={stu.name}
                                          />

                                          <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-2">
                                              <span className="fw-semibold">
                                                {stu.name}
                                              </span>

                                              {getBatchTitle(stu) && (
                                                <span className="badge bg-label-primary">
                                                  {getBatchTitle(stu)}
                                                </span>
                                              )}
                                            </div>

                                            <small className="text-muted">
                                              {stu.registration_no}
                                              {stu.phone && ` • ${stu.phone}`}
                                            </small>
                                          </div>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Staff */}
                          {filters.data_type === "student" && (
                            <div className="col-lg-4 col-md-6">
                              <div className="feedback-filter-label">Staff</div>

                              <div className="feedback-search">
                                <select
                                  className="form-select"
                                  value={filters.staff_id || ""}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      staff_id: e.target.value,
                                      page: 1,
                                    })
                                  }
                                >
                                  <option value="">All Staff</option>

                                  {staffList?.map((item) => (
                                    <option key={item.id} value={item.id}>
                                      {item.name} ({item.registration_no})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}

                          {/* Clear */}
                          {(filters.search ||
                            filters.staff_id ||
                            filters.student_id) && (
                            <div className="col-lg-auto col-md-2">
                              <button
                                type="button"
                                className="btn btn-light"
                                onClick={() => {
                                  setFilters({
                                    search: "",
                                    data_type: filters.data_type,
                                    page: 1,
                                  });

                                  setStudentSearch("");
                                  setStudents([]);
                                  setShowResult(false);
                                }}
                              >
                                <i className="ri-close-line me-1"></i>
                                Clear
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
            TABLE
        ===================================================== */}
                    <div className="card feedback-card feedback-table-card">
                      <div className="table-responsive">
                        <table className="table feedback-table align-middle">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Created By</th>
                              <th>Feedback For</th>
                              <th>Type</th>
                              <th>Rating</th>
                              {isSuperAdmin &&
                                filters.data_type === "student" && (
                                  <th>Reply</th>
                                )}
                              <th>Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {data?.data?.length > 0 ? (
                              data.data.map((item, i) => {
                                const creator =
                                  item?.user?.name ??
                                  item?.user?.staffname ??
                                  "Unknown";

                                const target =
                                  item?.target?.name ??
                                  item?.target?.staffname ??
                                  null;

                                return (
                                  <tr key={item.id || i}>
                                    {/* Number */}
                                    <td className="feedback-index">{i + 1}</td>

                                    {/* Creator */}
                                    <td>
                                      <div className="feedback-user">
                                        <Avatar
                                          person={item?.user}
                                          name={creator}
                                        />

                                        <div>
                                          <div className="feedback-user-name">
                                            {creator}
                                          </div>

                                          <div className="feedback-user-contact">
                                            <i className="ri-phone-line me-1"></i>
                                            {item?.user?.phone ??
                                              item?.user?.mobile ??
                                              "No contact"}
                                          </div>

                                          {getBatchTitle(item?.user) && (
                                            <span className="badge bg-label-primary mt-1">
                                              {getBatchTitle(item.user)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* Target */}
                                    <td>
                                      <div className="feedback-target">
                                        {item?.target_type === "management" ||
                                        item?.target_type === "system" ? (
                                          <div className="feedback-target-icon">
                                            <i
                                              className={
                                                item?.target_type ===
                                                "management"
                                                  ? "ri-building-line"
                                                  : "ri-computer-line"
                                              }
                                            ></i>
                                          </div>
                                        ) : (
                                          <Avatar
                                            person={item?.target}
                                            name={target}
                                          />
                                        )}

                                        <div>
                                          <div className="feedback-target-name">
                                            {target || item?.target_type || "-"}
                                          </div>

                                          {item?.target?.registration_no && (
                                            <div className="feedback-target-reg">
                                              {item.target.registration_no}
                                            </div>
                                          )}

                                          {getBatchTitle(item?.target) && (
                                            <span className="badge bg-label-primary mt-1">
                                              {getBatchTitle(item.target)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* Type */}
                                    <td>
                                      <span
                                        className={`feedback-type ${getTypeClass(
                                          item?.type,
                                        )}`}
                                      >
                                        <i
                                          className={
                                            item?.type === "complaint"
                                              ? "ri-error-warning-line"
                                              : item?.type === "suggestion"
                                                ? "ri-lightbulb-line"
                                                : "ri-heart-line"
                                          }
                                        ></i>

                                        {item?.type || "-"}
                                      </span>
                                    </td>

                                    {/* Rating */}
                                    <td>
                                      <RatingStars
                                        rating={item?.rating || 0}
                                        size={17}
                                      />
                                    </td>
                                    {/* Reply status */}
                                    {isSuperAdmin &&
                                      filters.data_type === "student" && (
                                        <td>
                                          {item?.reply_message ? (
                                            <span className="badge bg-label-success">
                                              <i className="ri-checkbox-circle-line me-1"></i>
                                              Replied
                                            </span>
                                          ) : (
                                            <span className="badge bg-label-secondary">
                                              Pending
                                            </span>
                                          )}
                                        </td>
                                      )}

                                    {/* Actions */}
                                    <td className="text-start">
                                      {hasAccess("/feedback/view") && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary feedback-action-btn me-1"
                                          title="View Feedback"
                                          onClick={() => {
                                            setSelectedItem(item);
                                            setShowDetailModal(true);
                                          }}
                                        >
                                          <i className="ri-eye-line"></i>
                                        </button>
                                      )}

                                      {hasAccess("/feedback/delete") && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger feedback-action-btn"
                                          title="Delete Feedback"
                                          onClick={() => handleDelete(item.id)}
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={
                                    isSuperAdmin &&
                                    filters.data_type === "student"
                                      ? "7"
                                      : "6"
                                  }
                                >
                                  <div className="feedback-empty">
                                    <div className="feedback-empty-icon">
                                      <i className="ri-feedback-line"></i>
                                    </div>

                                    <div className="feedback-empty-title">
                                      No feedback found
                                    </div>

                                    <div className="feedback-empty-text">
                                      Try changing your filters or add new
                                      feedback.
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {data?.data?.length > 0 && (
                        <div className="px-3 py-2 border-top">
                          <Pagination
                            data={data}
                            filters={filters}
                            setFilters={setFilters}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🔥 Modal */}
      {showDetailModal && (
        <>
          <div className="modal fade show d-block feedback-modal">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                {/* Header */}
                <div className="feedback-modal-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1 fw-bold">Feedback Details</h5>

                      <small>Review complete feedback information</small>
                    </div>

                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setShowDetailModal(false)}
                    ></button>
                  </div>
                </div>

                <div className="modal-body p-4">
                  {/* =================================================
                USER
            ================================================= */}
                  <div className="feedback-section">
                    <div className="feedback-section-title">
                      <i className="ri-user-line"></i>
                      Submitted By
                    </div>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <span className="feedback-detail-label">Name</span>

                        <span className="feedback-detail-value">
                          {selectedItem?.user?.name ||
                            selectedItem?.user?.staffname ||
                            "-"}
                        </span>
                      </div>

                      <div className="col-md-4">
                        <span className="feedback-detail-label">Mobile</span>

                        <span className="feedback-detail-value">
                          {selectedItem?.user?.mobile ||
                            selectedItem?.user?.phone ||
                            "-"}
                        </span>
                      </div>

                      <div className="col-md-4">
                        <span className="feedback-detail-label">User Type</span>

                        <span className="feedback-detail-value text-capitalize">
                          {selectedItem?.user_type || "-"}
                        </span>
                      </div>

                      {getBatchTitle(selectedItem?.user) && (
                        <div className="col-md-4">
                          <span className="feedback-detail-label">Batch</span>

                          <span className="feedback-detail-value">
                            {getBatchTitle(selectedItem.user)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* =================================================
                TARGET
            ================================================= */}
                  {selectedItem?.target_type !== "management" &&
                    selectedItem?.target_type !== "system" && (
                      <div className="feedback-section">
                        <div className="feedback-section-title">
                          <i className="ri-focus-3-line"></i>
                          Feedback For
                        </div>

                        <div className="row g-3">
                          <div className="col-md-4">
                            <span className="feedback-detail-label">Name</span>

                            <span className="feedback-detail-value">
                              {selectedItem?.target?.name ||
                                selectedItem?.target?.staffname ||
                                "-"}
                            </span>
                          </div>

                          <div className="col-md-4">
                            <span className="feedback-detail-label">Type</span>

                            <span className="feedback-detail-value text-capitalize">
                              {selectedItem?.target_type || "-"}
                            </span>
                          </div>

                          <div className="col-md-4">
                            <span className="feedback-detail-label">
                              Mobile
                            </span>

                            <span className="feedback-detail-value">
                              {selectedItem?.target?.phone ||
                                selectedItem?.target?.mobile ||
                                "-"}
                            </span>
                          </div>

                          {getBatchTitle(selectedItem?.target) && (
                            <div className="col-md-4">
                              <span className="feedback-detail-label">
                                Batch
                              </span>

                              <span className="feedback-detail-value">
                                {getBatchTitle(selectedItem.target)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {/* =================================================
                FEEDBACK
            ================================================= */}
                  <div className="feedback-section">
                    <div className="feedback-section-title">
                      <i className="ri-chat-3-line"></i>
                      Feedback
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <span className="feedback-detail-label">Category</span>

                        <span className="feedback-detail-value">
                          {selectedItem?.category || "-"}
                        </span>
                      </div>

                      <div className="col-md-4">
                        <span className="feedback-detail-label">Type</span>

                        <span
                          className={`feedback-type ${getTypeClass(
                            selectedItem?.type,
                          )}`}
                        >
                          {selectedItem?.type || "-"}
                        </span>
                      </div>

                      <div className="col-md-4">
                        <span className="feedback-detail-label">Rating</span>

                        <RatingStars
                          rating={selectedItem?.rating || 0}
                          size={18}
                        />

                        <div className="small text-muted mt-1">
                          {getRatingText(selectedItem?.rating)}
                        </div>
                      </div>
                    </div>

                    {/* Subject */}
                    <div className="mb-3">
                      <span className="feedback-detail-label">Subject</span>

                      <div className="fw-semibold text-dark">
                        {selectedItem?.subject || "-"}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <span className="feedback-detail-label">Message</span>

                      <div className="feedback-message">
                        {selectedItem?.message || "No message provided."}
                      </div>
                    </div>
                  </div>

                  {isSuperAdmin && selectedItem?.reply_message && (
                    <div className="feedback-section">
                      <div className="feedback-section-title">
                        <i className="ri-reply-line"></i>
                        Teacher's Reply
                      </div>

                      <div className="feedback-message feedback-reply-message">
                        {selectedItem.reply_message}
                      </div>

                      {selectedItem?.replied_at && (
                        <div className="small text-muted mt-2">
                          Replied on{" "}
                          {new Date(selectedItem.replied_at).toLocaleDateString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer border-top">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowDetailModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* =====================================================
    ADD FEEDBACK MODAL
===================================================== */}
      {showAddFeedbackModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content border-0 shadow rounded-4">
                {/* ================= HEADER ================= */}
                <div className="modal-header border-bottom">
                  <div>
                    <h5 className="modal-title fw-bold text-primary mb-1">
                      Add Feedback
                    </h5>

                    <small className="text-muted">
                      Feedback submitted by{" "}
                      <strong>
                        {userData?.name || userData?.staffname || "Teacher"}
                      </strong>
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddFeedbackModal(false)}
                  ></button>
                </div>

                {/* ================= BODY ================= */}
                <div className="modal-body">
                  {/* Feedback For */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Feedback For <span className="text-danger">*</span>
                    </label>

                    <div className="row g-2">
                      {/* Student */}
                      <div className="col-md-4">
                        <button
                          type="button"
                          className={`btn w-100 py-2 ${
                            feedbackForm.target_type === "student"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => {
                            setFeedbackForm((prev) => ({
                              ...prev,
                              target_type: "student",
                              target_id: "",
                            }));

                            setFeedbackStudentSearch("");
                            setFeedbackStudents([]);
                            setShowFeedbackStudentResult(false);
                          }}
                        >
                          <i className="ri-user-line me-1"></i>
                          Student
                        </button>
                      </div>

                      {/* Management */}
                      <div className="col-md-4">
                        <button
                          type="button"
                          className={`btn w-100 py-2 ${
                            feedbackForm.target_type === "management"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => {
                            setFeedbackForm((prev) => ({
                              ...prev,
                              target_type: "management",
                              target_id: "",
                            }));

                            setFeedbackStudentSearch("");
                            setFeedbackStudents([]);
                            setShowFeedbackStudentResult(false);
                          }}
                        >
                          <i className="ri-building-line me-1"></i>
                          Management
                        </button>
                      </div>

                      {/* System */}
                      <div className="col-md-4">
                        <button
                          type="button"
                          className={`btn w-100 py-2 ${
                            feedbackForm.target_type === "system"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => {
                            setFeedbackForm((prev) => ({
                              ...prev,
                              target_type: "system",
                              target_id: "",
                            }));

                            setFeedbackStudentSearch("");
                            setFeedbackStudents([]);
                            setShowFeedbackStudentResult(false);
                          }}
                        >
                          <i className="ri-computer-line me-1"></i>
                          System
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                STUDENT SEARCH
            ================================================= */}
                  {feedbackForm.target_type === "student" && (
                    <div className="mb-4 position-relative">
                      <label className="form-label fw-semibold">
                        Select Student <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name / registration no / mobile..."
                        value={feedbackStudentSearch}
                        onChange={(e) =>
                          handleFeedbackStudentSearch(e.target.value)
                        }
                      />

                      {/* Selected Student */}
                      {feedbackForm.target_id && (
                        <div className="mt-2">
                          <span className="badge bg-success">
                            <i className="ri-check-line me-1"></i>
                            Student Selected
                          </span>
                        </div>
                      )}

                      {/* Search Results */}
                      {showFeedbackStudentResult &&
                        feedbackStudents.length > 0 && (
                          <div
                            className="card position-absolute w-100 mt-1 shadow"
                            style={{
                              zIndex: 1055,
                              maxHeight: "250px",
                              overflowY: "auto",
                            }}
                          >
                            <div className="list-group list-group-flush">
                              {feedbackStudents.map((stu) => (
                                <button
                                  type="button"
                                  key={stu.id}
                                  className="list-group-item list-group-item-action text-start"
                                  onClick={() => {
                                    setFeedbackForm((prev) => ({
                                      ...prev,
                                      target_type: "student",
                                      target_id: stu.id,
                                    }));

                                    setFeedbackStudentSearch(
                                      `${stu.name} (${stu.registration_no})${
                                        getBatchTitle(stu)
                                          ? ` - ${getBatchTitle(stu)}`
                                          : ""
                                      }`,
                                    );

                                    setFeedbackStudents([]);
                                    setShowFeedbackStudentResult(false);
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <Avatar
                                      person={stu}
                                      name={stu.name}
                                      size={36}
                                    />

                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="fw-semibold">
                                          {stu.name}
                                        </span>

                                        {getBatchTitle(stu) && (
                                          <span className="badge bg-label-primary">
                                            {getBatchTitle(stu)}
                                          </span>
                                        )}
                                      </div>

                                      <small className="text-muted">
                                        {stu.registration_no}

                                        {stu.phone && (
                                          <>
                                            {" • "}
                                            {stu.phone}
                                          </>
                                        )}
                                      </small>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* No Result */}
                      {showFeedbackStudentResult &&
                        feedbackStudentSearch.length >= 2 &&
                        feedbackStudents.length === 0 && (
                          <div className="text-muted small mt-2">
                            No students found.
                          </div>
                        )}
                    </div>
                  )}

                  {/* =================================================
                SUBJECT
            ================================================= */}
                  <div className="form-floating form-floating-outline mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Subject"
                      value={feedbackForm.subject}
                      onChange={(e) =>
                        setFeedbackForm((prev) => ({
                          ...prev,
                          subject: e.target.value,
                        }))
                      }
                    />

                    <label>
                      Subject <span className="text-danger">*</span>
                    </label>
                  </div>

                  {/* =================================================
                TYPE + CATEGORY
            ================================================= */}
                  <div className="row">
                    {/* Type */}
                    <div className="col-md-6 mb-3">
                      <div className="form-floating form-floating-outline">
                        <select
                          className="form-select"
                          value={feedbackForm.type}
                          onChange={(e) =>
                            setFeedbackForm((prev) => ({
                              ...prev,
                              type: e.target.value,
                            }))
                          }
                        >
                          <option value="complaint">Complaint</option>

                          <option value="suggestion">Suggestion</option>

                          <option value="appreciation">Appreciation</option>
                        </select>

                        <label>Type</label>
                      </div>
                    </div>

                    {/* Category */}
                    <div className="col-md-6 mb-3">
                      <div className="form-floating form-floating-outline">
                        <select
                          className="form-select"
                          value={feedbackForm.category}
                          onChange={(e) =>
                            setFeedbackForm((prev) => ({
                              ...prev,
                              category: e.target.value,
                            }))
                          }
                        >
                          <option value="Teaching">Teaching</option>

                          <option value="Behaviour">Behaviour</option>

                          <option value="Doubt Solving">Doubt Solving</option>

                          <option value="Fees">Fees</option>

                          <option value="Facility">Facility</option>

                          <option value="Complaint">Complaint</option>

                          <option value="Suggestion">Suggestion</option>
                        </select>

                        <label>Category</label>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
    RATING
================================================= */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">Rating</label>

                    <div>
                      <RatingStars
                        rating={feedbackForm.rating}
                        interactive
                        size={30}
                        onChange={(rating) =>
                          setFeedbackForm((prev) => ({
                            ...prev,
                            rating,
                          }))
                        }
                      />

                      <div className="feedback-rating-text">
                        {feedbackForm.rating}/5 —{" "}
                        {getRatingText(feedbackForm.rating)}
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                MESSAGE
            ================================================= */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Message <span className="text-danger">*</span>
                    </label>

                    <textarea
                      className="form-control"
                      rows="5"
                      placeholder="Enter your feedback..."
                      value={feedbackForm.message}
                      onChange={(e) =>
                        setFeedbackForm((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                    ></textarea>
                  </div>

                  {/* =================================================
                ANONYMOUS
            ================================================= */}
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="feedbackAnonymous"
                      checked={feedbackForm.is_anonymous === "Yes"}
                      onChange={(e) =>
                        setFeedbackForm((prev) => ({
                          ...prev,
                          is_anonymous: e.target.checked ? "Yes" : "No",
                        }))
                      }
                    />

                    <label
                      className="form-check-label"
                      htmlFor="feedbackAnonymous"
                    >
                      Submit anonymously
                    </label>
                  </div>
                </div>

                {/* ================= FOOTER ================= */}
                <div className="modal-footer border-top">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={() => setShowAddFeedbackModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={handleSubmitFeedback}
                  >
                    <i className="ri-send-plane-line me-1"></i>
                    Submit Feedback
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}
